import React from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Calendar, FileText } from 'lucide-react';
import { motion } from 'framer-motion';
import JobStatusTable from './JobStatus';
import Pagination from '../../../component/Pagination';

const RecentJobsTabs = ({
  jobs,
  onDownload,
  onCancel,
  currentPage,
  totalPages,
  totalCount,
  currentCount,
  pageSize,
  activeTab,
  onPageChange,
  onPageSizeChange,
  onTabChange,
  candidatesCount,
  interviewsCount
}) => {
  const { t } = useTranslation();

  const renderEmptyState = (title, message) => (
    <div className="text-center py-20 bg-gray-50/50 rounded-3xl border border-dashed border-gray-200">
      <div className="mx-auto w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center mb-4">
        <FileText size={32} className="text-gray-300" />
      </div>
      <h3 className="text-lg font-bold text-gray-900">{title}</h3>
      <p className="text-gray-500 mt-2 text-sm">{message}</p>
    </div>
  );

  const renderJobsTable = (jobsList) => {
    if (jobsList.length === 0) {
      if (totalCount > 0) {
        return renderEmptyState(t('report.noReportsOnPage'), t('report.tryDifferentPage'));
      }
      return renderEmptyState(t('report.noCompletedReports'), t('report.selectTypeToGenerate'));
    }

    return (
      <JobStatusTable
        jobs={jobsList}
        onDownload={onDownload}
        onCancel={onCancel}
      />
    );
  };

  const TabButton = ({ isActive, onClick, icon: Icon, label, count }) => {
    return (
      <button
        onClick={onClick}
        className={`relative flex items-center gap-2 px-6 py-3 text-sm font-bold rounded-full transition-all duration-300 border border-gray-300
          ${isActive ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600 hover:border-gray-400'}
        `}
      >
        {isActive && (
          <motion.div
            layoutId="activeTabPill"
            className="absolute inset-0 bg-white shadow-sm border border-gray-100 rounded-full"
            transition={{ type: "spring", bounce: 0.2, duration: 0.6 }}
          />
        )}
        <span className="relative z-10 flex items-center gap-2">
          <Icon size={16} />
          {label}
          {count > 0 && (
            <span className={`ml-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold ${isActive ? 'bg-gray-100 text-gray-900' : 'bg-gray-100/50 text-gray-400'}`}>
              {count}
            </span>
          )}
        </span>
      </button>
    );
  };

  return (
    <div>
      <div className="flex justify-center mb-2">
        <div className="inline-flex bg-gray-100/50 p-0.5 rounded-full">
          <TabButton
            isActive={activeTab === 'candidates'}
            onClick={() => onTabChange('candidates')}
            icon={Users}
            label={t('report.candidatesReports')}
            count={candidatesCount}
          />
          <TabButton
            isActive={activeTab === 'interviews'}
            onClick={() => onTabChange('interviews')}
            icon={Calendar}
            label={t('report.interviewsReports')}
            count={interviewsCount}
          />
        </div>
      </div>

      <div className="min-h-[300px]">
        {renderJobsTable(jobs)}
      </div>

      {totalCount > 0 && (
        <div className="pt-2 border-t border-gray-100 mt-2 mb-8">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            currentCount={currentCount}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            pageSize={pageSize}
            pageSizeOptions={[5, 10, 25, 50]}
            showPageSize={false}
          />
        </div>
      )}
    </div>
  );
};

export default RecentJobsTabs;